import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Instagram, Youtube, Facebook, MessageCircle, Mail } from "lucide-react";
import logoWide from "@/assets/drsara-logo-cream.png.asset.json";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { CONTACT_EMAIL } from "@/lib/legal/seller";
import { openConsentSettings } from "@/lib/consent";
import { SOCIAL, instagramUrl, supportWhatsAppUrl } from "@/lib/social";
import { TikTokIcon, ThreadsIcon } from "@/components/public/social-icons";
import { SITE_NAV } from "@/components/public/site-nav";
import { formatPhoneGrouped } from "@/data/about-contact";

// Same year on server and browser: fixed clinic time zone, never the visitor's.
function footerYear() {
  return new Intl.DateTimeFormat("en-GB", { year: "numeric", timeZone: "Asia/Damascus" }).format(new Date());
}

const linkCls = "inline-flex min-h-11 items-center text-[15px] text-cream-300 transition-colors duration-ds-fast ease-ds hover:text-cream-100";
const socialCls =
  "grid h-11 w-11 place-items-center rounded-full text-brand-on ring-1 ring-cream-100/15 transition-opacity duration-ds-fast ease-ds hover:opacity-90";
const titleCls = "font-display text-[17px] font-bold text-cream-100";
const contactCard =
  "flex min-h-[64px] items-center gap-3 rounded-ds-lg bg-cream-100/8 px-4 py-3 transition-colors duration-ds-fast ease-ds hover:bg-cream-100/12";

export function SiteFooter() {
  // Loader prefetches this via ensureQueryData on /_public. useQuery keeps
  // it non-suspending so a settings failure never blanks the footer.
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  const instagram = instagramUrl(settings?.instagram_url);
  const clinicNumber = settings?.clinic_whatsapp?.trim() || "";
  const whatsappUrl =
    settings?.whatsapp_community_url?.trim() || supportWhatsAppUrl(settings?.clinic_whatsapp);
  const clinicUrl = clinicNumber ? supportWhatsAppUrl(clinicNumber) : "";
  const platform = SITE_NAV.filter((n) => n.to !== "/");
  return (
    <footer className="mt-24 rounded-t-ds-2xl bg-inverse pb-8 pt-16 text-cream-100">
      <div className="mx-auto w-[min(100%-2.5rem,76rem)]">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr_1.6fr]">
          {/* Brand */}
          <div>
            <img
              src={logoWide.url}
              alt="د. سارة النحّاس"
              width={1000}
              height={460}
              className="h-10 w-auto"
            />
            <p className="mt-4 text-[15px] leading-[1.8] text-cream-300">استشارات تربوية ونفسية، أونلاين من أي بلد وبالعيادة بدمشق.</p>
            <p className="mt-2 text-[14px] text-cream-300">دكتوراه في التربية – جامعة دمشق</p>
          </div>

          {/* Contact + social */}
          <div>
            <p className={titleCls}>التواصل</p>
            <div className="mt-3 grid gap-3">
              {clinicUrl && (
                <a href={clinicUrl} target="_blank" rel="noopener noreferrer" className={contactCard}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-whatsapp text-brand-on">
                    <MessageCircle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <span>
                    <span className="block text-[13px] text-cream-300">واتساب العيادة</span>
                    <span dir="ltr" className="block font-display text-[16px] font-bold text-cream-100">{formatPhoneGrouped(clinicNumber)}</span>
                  </span>
                </a>
              )}
              <a href={`mailto:${CONTACT_EMAIL}`} className={contactCard}>
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-cream-100/12 text-cream-100">
                  <Mail className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                </span>
                <span>
                  <span className="block text-[13px] text-cream-300">البريد</span>
                  <span dir="ltr" className="block font-display text-[16px] font-bold text-cream-100">{CONTACT_EMAIL}</span>
                </span>
              </a>
            </div>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="واتساب" className={socialCls + " bg-brand-whatsapp"}>
                  <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
                </a>
              )}
              <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="إنستغرام" className={socialCls + " bg-instagram-2"}>
                <Instagram className="h-5 w-5" strokeWidth={1.75} />
              </a>
              <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="تيك توك" className={socialCls + " bg-brand-tiktok"}>
                <TikTokIcon className="h-5 w-5" />
              </a>
              <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" aria-label="يوتيوب" className={socialCls + " bg-brand-youtube"}>
                <Youtube className="h-5 w-5" strokeWidth={1.75} />
              </a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="فيسبوك" className={socialCls + " bg-brand-facebook"}>
                <Facebook className="h-5 w-5" strokeWidth={1.75} />
              </a>
              <a href={SOCIAL.threads} target="_blank" rel="noopener noreferrer" aria-label="ثريدز" className={socialCls + " bg-brand-threads"}>
                <ThreadsIcon className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            <div>
              <p className={titleCls}>المنصّة</p>
              <ul className="mt-3">
                {platform.map((n) => (
                  <li key={n.to}><Link to={n.to as any} className={linkCls}>{n.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <p className={titleCls}>حسابك</p>
              <ul className="mt-3">
                <li><Link to="/auth" search={{ mode: "sign_in" } as any} className={linkCls}>تسجيل الدخول</Link></li>
                <li><Link to="/portal" className={linkCls}>لوحتي</Link></li>
                <li><Link to="/book" className={linkCls}>حجز استشارة</Link></li>
              </ul>
            </div>
            <div>
              <p className={titleCls}>قانوني</p>
              <ul className="mt-3">
                <li><Link to="/help" className={linkCls}>مركز المساعدة</Link></li>
                <li><Link to="/privacy" className={linkCls}>الخصوصية</Link></li>
                <li><Link to="/terms" className={linkCls}>الشروط</Link></li>
                <li><Link to="/refund" className={linkCls}>الاسترداد والإلغاء</Link></li>
                <li><Link to="/disclaimer" className={linkCls}>إخلاء المسؤولية</Link></li>
                <li><Link to="/editorial-policy" className={linkCls}>سياسة التحرير</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-cream-100/15 pt-5 text-[14px] text-cream-300 lg:flex-row lg:items-center lg:justify-between">
          <span className="whitespace-nowrap">© {footerYear()} د. سارة النحّاس</span>
          <span>المحتوى تثقيفي عام وليس بديلاً عن التشخيص.</span>
          <button type="button" onClick={openConsentSettings} className={linkCls + " text-[14px]"}>إعدادات الخصوصية</button>
        </div>
      </div>
    </footer>
  );
}
